import type { Invoice } from "$global-providers/rxdb-provider/collections/invoice";
import { Invoice as InvoiceModel } from "$routes/_lib/billing-v2/invoice";
import type { InvoiceLineSource } from "$routes/_lib/billing-v2/invoice-source";
import {
  type BillingCurrency,
  InvoiceStatus,
  PatientType,
  PayerType,
} from "elephant-web/gql";
import type { RxCollection } from "rxdb";
import { invoiceNumberFromId } from "../invoice-number";

export type FindArgs = {
  patientId: string;
  facilityId: string;
  invoiceCollection: RxCollection<Invoice>;
};

export const findActivePendingInvoice = async ({
  patientId,
  facilityId,
  invoiceCollection,
}: FindArgs): Promise<Invoice | null> => {
  const existing = await invoiceCollection
    .findOne({
      selector: {
        patientId,
        facilityId,
        invoiceStatus: InvoiceStatus.Pending,
      },
    })
    .exec();
  return existing ? (existing.toJSON() as Invoice) : null;
};

export type CreateArgs = {
  id: string;
  payerId: string;
  timestamp: string;
  patientId: string;
  facilityId: string;
  currency: BillingCurrency;
  actorId: string;
  initialLineItems: InvoiceLineSource[];
  invoiceCollection: RxCollection<Invoice>;
};

/**
 * Creates a new PENDING invoice with `initialLineItems` already attached.
 * Single insert — never leaves an empty PENDING visible to the cashier even
 * if subsequent writes fail.
 */
export const createPendingInvoice = async ({
  id,
  payerId,
  timestamp,
  patientId,
  facilityId,
  currency,
  actorId,
  initialLineItems,
  invoiceCollection,
}: CreateArgs): Promise<Invoice> => {
  const meta = {
    createdAt: timestamp,
    createdByUserId: actorId,
    deleted: false,
    updatedAt: timestamp,
    updatedByUserId: actorId,
  };

  const newInvoice = InvoiceModel.from({
    id,
    facilityId,
    patientId,
    patientType: PatientType.Outpatient,
    invoiceNumber: invoiceNumberFromId(id),
    currency,
    phase: "PENDING",
    inpatientDetails: null,
    lineItems: initialLineItems,
    payers: [
      {
        id: payerId,
        name: "Patient",
        type: PayerType.Patient,
        insuranceDetails: null,
      },
    ],
    transactions: [],
    meta,
    finalizedAt: null,
    finalizedByUserId: null,
    priceListLastSyncedAt: null,
  }).toRecord();

  await invoiceCollection.insert(newInvoice);
  return newInvoice;
};
